import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { useState, type ComponentProps } from "react";
import {
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
} from "@volca/ui/components/ui/dropdown-menu";
import { Input } from "@volca/ui/components/ui/input";
import { Text } from "@volca/ui/components/ui/text";
import { useApiFileHref } from "@/core/api/api-file-href";
import { useAuth } from "@/core/auth";
import { useBusiness } from "@/core/business";
import { useGetMyUser } from "@/user/query";

export const workspaceMark = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

export const WorkspaceRailTrigger = ({
  name,
  src,
  ...props
}: {
  name: string | null;
  src?: string | null;
} & Omit<ComponentProps<"button">, "name">) => {
  const href = useApiFileHref(src);
  return (
    <button
      type="button"
      {...props}
      className="grid size-8 place-items-center overflow-hidden rounded-md bg-neutral text-xs leading-none font-medium text-subtle outline-hidden transition-[background-color] duration-150 ease-out hover:bg-neutral-hovered focus-visible:ring-2 focus-visible:ring-focus data-popup-open:bg-neutral-hovered motion-safe:active:translate-y-px"
    >
      {href ? (
        <img src={href} alt="" className="size-full object-contain" />
      ) : name ? (
        workspaceMark(name) || "?"
      ) : (
        <span
          data-slot="skeleton"
          className="size-full animate-pulse bg-neutral-hovered"
        />
      )}
    </button>
  );
};

export const WorkspaceLogo = ({
  name,
  src,
}: {
  name: string;
  src?: string | null;
}) => {
  const href = useApiFileHref(src);
  return href ? (
    <img
      src={href}
      alt=""
      className="size-5 shrink-0 rounded-md object-contain"
    />
  ) : (
    <span
      aria-hidden
      className="grid size-5 shrink-0 place-items-center rounded-md bg-neutral text-xs leading-none font-medium text-subtle"
    >
      {workspaceMark(name) || "?"}
    </span>
  );
};

export const WorkspaceBusinesses = ({
  onSelect,
  showIdentity = true,
}: {
  onSelect: () => void;
  showIdentity?: boolean;
}) => {
  const { user } = useAuth();
  const { data: userInfo } = useGetMyUser();
  const {
    businessId,
    businesses,
    selectedBusinessId,
    isTenantUser,
    selectBusiness,
  } = useBusiness();
  const [query, setQuery] = useState("");
  const email = userInfo?.email ?? user?.email ?? "";
  const workspaceQuery = query.trim().toLowerCase();
  const searchable = businesses.length > 8;
  const visibleWorkspaces =
    searchable && workspaceQuery
      ? businesses.filter((business) =>
          business.name.toLowerCase().includes(workspaceQuery),
        )
      : businesses;

  return (
    <DropdownMenuGroup>
      {showIdentity && isTenantUser && userInfo?.parentable_name && (
        <DropdownMenuLabel className="break-words whitespace-normal">
          {userInfo.parentable_name}
        </DropdownMenuLabel>
      )}
      {showIdentity && email && (
        <DropdownMenuLabel className="truncate" title={email}>
          {email}
        </DropdownMenuLabel>
      )}
      {searchable && (
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => event.stopPropagation()}
          placeholder="Search workspaces"
          aria-label="Search workspaces"
          className="mx-1.5 mb-1"
        />
      )}
      {visibleWorkspaces.length === 0 ? (
        <Text tone="muted" className="px-1.5 py-2">
          No workspace matches.
        </Text>
      ) : (
        visibleWorkspaces.map((business) => {
          const current = business.id === (businessId ?? selectedBusinessId);
          return (
            <DropdownMenuItem
              key={business.id}
              onClick={() => {
                selectBusiness(business.id);
                onSelect();
              }}
            >
              <WorkspaceLogo name={business.name} src={business.logo_url} />
              <span
                className="min-w-0 flex-1 break-words whitespace-normal"
                title={business.name}
              >
                {business.name}
              </span>
              {current && (
                <CheckIcon className="ml-auto" aria-label="Current" />
              )}
            </DropdownMenuItem>
          );
        })
      )}
    </DropdownMenuGroup>
  );
};
